import { BoundingSphere, Color, ColorGeometryInstanceAttribute, ComponentDatatype, Geometry, GeometryAttribute, GeometryInstance, GeometryPipeline, Material, Matrix4, PerInstanceColorAppearance, PolylineCollection, Primitive, PrimitiveType, ShowGeometryInstanceAttribute, VertexFormat, } from "cesium";
import { SpreadingCompactionGeometry } from "./spreading-compaction-geometry";
import { chainEdgeSegments, extractFeatureEdges } from "./feature-edge";
const OUTLINE_FEATURE_EDGE_ANGLE_DEG = 30;
const DEFAULT_COLORS = {
    spreading: Color.fromCssColorString("#4FC3F7"),
    compaction: Color.fromCssColorString("#FF8A65"),
};
const DEFAULT_ALPHA = 0.7;
/**
 * Manages a group of SpreadingCompactionGeometry objects for a single execution.
 *
 * Two layers of visibility:
 *   - `visible` (whole result): list eye-icon. false → 3D all hidden, crossSection excluded.
 *   - `_surfaceVisibility` (per offset): checkboxes. Controls individual 3D show/hide.
 *
 * 3D shown:       visible && _surfaceVisibility.get(key)
 * CrossSection:   visible → all compaction surfaces traced (spreading excluded).
 *                 emphasized=true → 2D highlight color, false → 2D muted color.
 */
export class SpreadingCompaction {
    _surfaces = new Map();
    _wgs84Geometries = new Map();
    _surfaceVisibility = new Map();
    _surfacesByType = null;
    _viewer;
    _visible = true;
    _opacity = DEFAULT_ALPHA;
    _fillPrimitives = new Map();
    _wireframePrimitives = new Map();
    _outlineCollections = new Map();
    _outlinePolylines = new Map();
    _pendingApplyKeys = new Set();
    _removePendingListener;
    constructor(viewer) {
        this._viewer = viewer;
    }
    static surfaceKey(type, offset) {
        return `${type}_${offset}`;
    }
    get visible() {
        return this._visible;
    }
    // --- Load ---
    async create(input, convertToWgs84) {
        this.disposeAll();
        const entries = input.surfaces.map((surfaceData) => ({
            surfaceData,
            key: SpreadingCompaction.surfaceKey(surfaceData.type, surfaceData.offset),
        }));
        const wgs84Results = await Promise.all(entries.map(({ surfaceData }) => convertToWgs84(surfaceData.geometry)));
        for (let i = 0; i < entries.length; i++) {
            const { surfaceData, key } = entries[i];
            const surface = new SpreadingCompactionGeometry(surfaceData.geometry, surfaceData.type, surfaceData.offset);
            this._surfaces.set(key, surface);
            this._wgs84Geometries.set(key, wgs84Results[i]);
            this._surfaceVisibility.set(key, true);
        }
        this._rebuildFillPrimitives();
        this._rebuildOutlineCollections();
    }
    // --- Visibility ---
    setVisible(visible) {
        this._visible = visible;
        for (const key of this._surfaces.keys()) {
            this._applySurfaceAppearance(key);
        }
    }
    setSurfaceVisible(type, offset, visible) {
        const key = SpreadingCompaction.surfaceKey(type, offset);
        if (!this._surfaces.has(key))
            return;
        this._surfaceVisibility.set(key, visible);
        this._applySurfaceAppearance(key);
    }
    setAllSurfaceVisible(visible) {
        for (const key of this._surfaces.keys()) {
            this._surfaceVisibility.set(key, visible);
            this._applySurfaceAppearance(key);
        }
    }
    isSurfaceVisible(key) {
        return this._visible && (this._surfaceVisibility.get(key) ?? false);
    }
    isSurfaceVisibleByTypeOffset(type, offset) {
        return this.isSurfaceVisible(SpreadingCompaction.surfaceKey(type, offset));
    }
    // --- Opacity ---
    setOpacity(alpha) {
        this._opacity = alpha;
        for (const key of this._surfaces.keys()) {
            this._applySurfaceAppearance(key);
        }
    }
    // --- Accessors ---
    getTraceables() {
        return [...this._surfaces.values()];
    }
    getSurface(type, offset) {
        return this._surfaces.get(SpreadingCompaction.surfaceKey(type, offset));
    }
    getAllSurfaces() {
        return this._surfaces;
    }
    getAllGeometries() {
        const geometries = [];
        for (const surface of this._surfaces.values()) {
            geometries.push({
                type: surface.type,
                offset: surface.offset,
                geometry: surface.geometry,
            });
        }
        return geometries;
    }
    get hasSurfaces() {
        return this._surfaces.size > 0;
    }
    // --- Dispose ---
    disposeAll() {
        if (this._removePendingListener) {
            this._removePendingListener();
            this._removePendingListener = undefined;
        }
        this._pendingApplyKeys.clear();
        for (const primitive of this._fillPrimitives.values()) {
            this._destroyPrimitive(primitive);
        }
        for (const primitive of this._wireframePrimitives.values()) {
            this._destroyPrimitive(primitive);
        }
        for (const collection of this._outlineCollections.values()) {
            this._destroyPolylineCollection(collection);
        }
        this._fillPrimitives.clear();
        this._wireframePrimitives.clear();
        this._outlineCollections.clear();
        this._outlinePolylines.clear();
        this._surfaces.clear();
        this._wgs84Geometries.clear();
        this._surfaceVisibility.clear();
        this._surfacesByType = null;
    }
    // --- Private: rendering ---
    _getSurfacesByType(type) {
        if (!this._surfacesByType) {
            this._surfacesByType = new Map();
            for (const [key, surface] of this._surfaces) {
                const list = this._surfacesByType.get(surface.type);
                if (list) {
                    list.push([key, surface]);
                }
                else {
                    this._surfacesByType.set(surface.type, [[key, surface]]);
                }
            }
        }
        return this._surfacesByType.get(type) ?? [];
    }
    _getFillColor(type) {
        const base = DEFAULT_COLORS[type];
        return new Color(base.red, base.green, base.blue, this._opacity);
    }
    // Outline shares opacity with the surface it traces — they are the same
    // renderable from the user's perspective.
    _getOutlineColor() {
        return Color.WHITE.withAlpha(this._opacity);
    }
    _createGeometry(geometry, wireframe = false) {
        const cesiumGeometry = new Geometry({
            // @ts-expect-error Cesium typing mismatch - position-only geometry is valid
            attributes: {
                position: new GeometryAttribute({
                    componentDatatype: ComponentDatatype.DOUBLE,
                    componentsPerAttribute: 3,
                    values: new Float64Array(geometry.vertices),
                }),
            },
            vertexFormat: VertexFormat.POSITION_ONLY,
            indices: new Uint32Array(geometry.indices),
            primitiveType: PrimitiveType.TRIANGLES,
            boundingSphere: BoundingSphere.fromVertices(geometry.vertices),
        });
        GeometryPipeline.computeNormal(cesiumGeometry);
        if (wireframe) {
            GeometryPipeline.toWireframe(cesiumGeometry);
        }
        return cesiumGeometry;
    }
    _createPrimitiveForType(type, wireframe = false) {
        const surfaces = this._getSurfacesByType(type);
        if (surfaces.length === 0)
            return null;
        const geometryInstances = surfaces.map(([key, surface]) => new GeometryInstance({
            geometry: this._createGeometry(this._wgs84Geometries.get(key) ?? surface.geometry, wireframe),
            modelMatrix: Matrix4.IDENTITY,
            attributes: {
                color: ColorGeometryInstanceAttribute.fromColor(wireframe ? Color.WHITE.withAlpha(0.8) : this._getFillColor(type)),
                show: new ShowGeometryInstanceAttribute(this.isSurfaceVisible(key)),
            },
            id: key,
        }));
        const primitive = new Primitive({
            geometryInstances,
            allowPicking: false,
            appearance: new PerInstanceColorAppearance({
                flat: false,
                translucent: true,
                faceForward: true,
            }),
            asynchronous: false,
        });
        this._viewer.scene.primitives.add(primitive);
        primitive.show = true;
        return primitive;
    }
    _rebuildFillPrimitives() {
        for (const primitive of this._fillPrimitives.values()) {
            this._destroyPrimitive(primitive);
        }
        this._fillPrimitives.clear();
        for (const type of ["spreading", "compaction"]) {
            const primitive = this._createPrimitiveForType(type, false);
            if (primitive) {
                this._fillPrimitives.set(type, primitive);
            }
        }
        this._rebuildWireframePrimitives();
    }
    _rebuildWireframePrimitives() {
        for (const primitive of this._wireframePrimitives.values()) {
            this._destroyPrimitive(primitive);
        }
        this._wireframePrimitives.clear();
        for (const type of ["spreading", "compaction"]) {
            const primitive = this._createPrimitiveForType(type, true);
            if (primitive) {
                this._wireframePrimitives.set(type, primitive);
            }
        }
    }
    _rebuildOutlineCollections() {
        for (const collection of this._outlineCollections.values()) {
            this._destroyPolylineCollection(collection);
        }
        this._outlineCollections.clear();
        this._outlinePolylines.clear();
        for (const type of ["spreading", "compaction"]) {
            const surfaces = this._getSurfacesByType(type);
            if (surfaces.length === 0)
                continue;
            const collection = new PolylineCollection();
            this._viewer.scene.primitives.add(collection);
            this._outlineCollections.set(type, collection);
            for (const [key] of surfaces) {
                const wgs84Geom = this._wgs84Geometries.get(key);
                if (!wgs84Geom)
                    continue;
                const strips = chainEdgeSegments(wgs84Geom, extractFeatureEdges(wgs84Geom, OUTLINE_FEATURE_EDGE_ANGLE_DEG));
                const polylines = strips.map((strip) => collection.add({
                    positions: [...strip],
                    width: 2,
                    material: Material.fromType("Color", {
                        color: this._getOutlineColor(),
                    }),
                    show: this.isSurfaceVisible(key),
                }));
                this._outlinePolylines.set(key, polylines);
            }
        }
    }
    /**
     * Atomic apply: sync all 3 layers (fill / wireframe / outline) from `_surfaceVisibility`.
     * If any Cesium Primitive is not yet ready, the apply is deferred via scene.postRender
     * so layers never diverge (e.g. outline visible without fill).
     */
    _applySurfaceAppearance(key) {
        const surface = this._surfaces.get(key);
        if (!surface)
            return;
        if (this._isReadyForKey(surface.type)) {
            this._applyAtomic(key);
        }
        else {
            this._enqueuePending(key);
        }
    }
    _isReadyForKey(type) {
        const fill = this._fillPrimitives.get(type);
        const wire = this._wireframePrimitives.get(type);
        return (!fill || fill.ready) && (!wire || wire.ready);
    }
    _applyAtomic(key) {
        const surface = this._surfaces.get(key);
        if (!surface)
            return;
        const shown = this.isSurfaceVisible(key);
        const fillPrimitive = this._fillPrimitives.get(surface.type);
        if (fillPrimitive && !fillPrimitive.isDestroyed()) {
            const attrs = fillPrimitive.getGeometryInstanceAttributes(key);
            if (attrs) {
                attrs.show = ShowGeometryInstanceAttribute.toValue(shown, attrs.show);
                attrs.color = ColorGeometryInstanceAttribute.toValue(this._getFillColor(surface.type), attrs.color);
            }
        }
        const wireframePrimitive = this._wireframePrimitives.get(surface.type);
        if (wireframePrimitive && !wireframePrimitive.isDestroyed()) {
            const attrs = wireframePrimitive.getGeometryInstanceAttributes(key);
            if (attrs) {
                attrs.show = ShowGeometryInstanceAttribute.toValue(shown, attrs.show);
            }
        }
        const outlineColor = this._getOutlineColor();
        for (const polyline of this._outlinePolylines.get(key) ?? []) {
            polyline.show = shown;
            polyline.material.uniforms.color = outlineColor;
        }
    }
    _enqueuePending(key) {
        this._pendingApplyKeys.add(key);
        if (this._removePendingListener)
            return;
        this._removePendingListener =
            this._viewer.scene.postRender.addEventListener(() => this._drainPending());
    }
    _drainPending() {
        for (const key of [...this._pendingApplyKeys]) {
            const surface = this._surfaces.get(key);
            if (!surface) {
                this._pendingApplyKeys.delete(key);
                continue;
            }
            if (this._isReadyForKey(surface.type)) {
                this._applyAtomic(key);
                this._pendingApplyKeys.delete(key);
            }
        }
        if (this._pendingApplyKeys.size === 0 && this._removePendingListener) {
            this._removePendingListener();
            this._removePendingListener = undefined;
        }
    }
    _destroyPrimitive(primitive) {
        if (primitive.isDestroyed())
            return;
        this._viewer.scene.primitives.remove(primitive);
    }
    _destroyPolylineCollection(collection) {
        if (collection.isDestroyed())
            return;
        this._viewer.scene.primitives.remove(collection);
    }
}
//# sourceMappingURL=spreading-compaction.js.map